import { array, nonEmptyArray, number, option, ord } from "fp-ts";
import { pipe } from "fp-ts/function";
import { contramap, Ord } from "fp-ts/Ord";
import { concatAll } from "fp-ts/Monoid";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { Option } from "fp-ts/Option";
import { StoredTimeEntry } from "../../../domain/timing";
import { Distance } from "../../../domain/common";
import { paceMaxDefault } from "../../../domain/event";
import { EventDetailRace, RaceSegment } from "../../../hooks/event";
import { Nullable } from "../../../utils/types";

export interface Checkpoint {
    timeLimit: StoredTimeEntry;
    distance: Distance;
}

interface WithOrdering {
    ordering: number;
}
const ordByOrderingAsc = <T extends WithOrdering>() =>
    pipe(
        number.Ord,
        contramap((p: T) => p.ordering)
    );

export const checkpointsFromRace = (
    race: EventDetailRace
): Option<NonEmptyArray<Checkpoint>> =>
    pipe(
        race.race_segment, // TODO make configurable in timing setup, segments does not have to be 1:1 with bibtag setup
        array.sort(ordByOrderingAsc<RaceSegment>()),
        array.map((raceSegmentItem) => raceSegmentItem.segment),
        (segments) =>
            pipe(
                race.fastest_expected_pace,
                option.fromNullable,
                option.getOrElse(() => paceMaxDefault(race.sport)),
                (pace) => {
                    const initAcc: Checkpoint = {
                        distance: 0 as Distance,
                        timeLimit: 0 as StoredTimeEntry,
                    };

                    return pipe(
                        segments,
                        array.scanLeft(
                            initAcc,
                            (acc, segment): Checkpoint => ({
                                distance: (acc.distance +
                                    segment.distance) as Distance, // TODO calculus
                                timeLimit: (pace *
                                    segment.distance) as StoredTimeEntry,
                            })
                        ),
                        array.dropLeft(1) // cleanup after scanLeft
                    );
                }
            ),
        nonEmptyArray.fromArray
    );

export interface PassForIgnoreComputation {
    ignored: Nullable<boolean>;
    startRelativeTimeEntry: StoredTimeEntry;
}

const ordByStartRelativeTimeEntry = contramap(
    (item: PassForIgnoreComputation) => item.startRelativeTimeEntry
)(number.Ord);

export interface ComputedIgnoreDeps {
    checkpointList: NonEmptyArray<Checkpoint>;
    fluidLapsCount: boolean;
}

export const computeIgnored =
    (d: ComputedIgnoreDeps) =>
    <T extends PassForIgnoreComputation>(
        bibPassList: NonEmptyArray<T>
    ): Array<T & { computedIgnore: boolean }> => {
        const bibPassListSorted = pipe(
            bibPassList,
            nonEmptyArray.sort(ordByStartRelativeTimeEntry)
        );

        // TODO sort checkpoints as well?

        let checkPointIndex = 0;
        const result: Array<T & { computedIgnore: boolean }> = [];
        let usedTimeSum = 0;

        for (
            let passIndex = 0;
            passIndex < bibPassListSorted.length;
            passIndex++
        ) {
            const pass = bibPassListSorted[passIndex];

            if (checkPointIndex >= d.checkpointList.length) {
                if (d.fluidLapsCount) {
                    checkPointIndex = 0;
                } else {
                    break;
                }
            }

            const checkpointAssigned = d.checkpointList[checkPointIndex];
            const segmentTime = pass.startRelativeTimeEntry - usedTimeSum;

            if (!pass.ignored && segmentTime >= checkpointAssigned.timeLimit) {
                result.push({
                    ...pass,
                    computedIgnore: false,
                });

                usedTimeSum = usedTimeSum + segmentTime;
                checkPointIndex++;
            } else {
                result.push({
                    ...pass,
                    computedIgnore: true,
                });
            }
        }

        return result;
    };

export const isPassNotIgnored = <
    T extends Pick<PassForIgnoreComputation, "ignored"> & {
        computedIgnore: boolean;
    }
>(
    pass: T
) => !pass.ignored && !pass.computedIgnore;

export interface WithLapsAndSum {
    laps: number;
    checkpointSum: StoredTimeEntry;
}

const M = ord.getMonoid<WithLapsAndSum>();
const ordByOrdLapsDesc = pipe(
    number.Ord,
    contramap((item: WithLapsAndSum) => item.laps),
    ord.reverse
);
const ordByCheckpointSum = pipe(
    number.Ord,
    contramap((item: WithLapsAndSum) => item.checkpointSum)
);
export const lapsRaceOrd = concatAll(M)([ordByOrdLapsDesc, ordByCheckpointSum]);

interface RankAcc<T> {
    rank: number;
    withSameClassification: number;
    prev: T;
}

export const addRank =
    <T>(ord: Ord<T>) =>
    (rows: NonEmptyArray<T>): Array<T & { rank: number }> => {
        const initAcc: RankAcc<T> = {
            rank: 1,
            withSameClassification: 0,
            prev: nonEmptyArray.head(rows),
        };

        return pipe(
            rows,
            nonEmptyArray.sort(ord),
            array.scanLeft(initAcc, (acc, current): RankAcc<T> => {
                const isAfterPrevResult = !ord.equals(current, acc.prev);

                const rankDiff = isAfterPrevResult
                    ? acc.withSameClassification
                    : 0;

                const withSameClassification = isAfterPrevResult
                    ? 1
                    : acc.withSameClassification + 1;

                return {
                    rank: acc.rank + rankDiff,
                    withSameClassification,
                    prev: current,
                };
            }),
            array.dropLeft(1),
            array.map((r) => ({
                ...r.prev,
                rank: r.rank,
            }))
        );
    };
